import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type Language = 'en' | 'nl';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<Language>('nl'); // Default to Dutch

  useEffect(() => {
    // Set initial language based on localStorage or i18n current language
    const storedLang = localStorage.getItem('language') as Language | null;
    if (storedLang === 'en' || storedLang === 'nl') {
      setLanguageState(storedLang);
      i18n.changeLanguage(storedLang);
    } else {
      const currentLang = i18n.language as Language;
      if (currentLang === 'en' || currentLang === 'nl') {
        setLanguageState(currentLang);
      }
    }
  }, [i18n.language]);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};